import { useState, useEffect, useRef } from 'react'
import SpaceBetween from '@cloudscape-design/components/space-between'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Button from '@cloudscape-design/components/button'
import Input from '@cloudscape-design/components/input'
import Textarea from '@cloudscape-design/components/textarea'
import FormField from '@cloudscape-design/components/form-field'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import Box from '@cloudscape-design/components/box'
import Badge from '@cloudscape-design/components/badge'
import Flashbar from '@cloudscape-design/components/flashbar'
import Select from '@cloudscape-design/components/select'
import type { SelectProps } from '@cloudscape-design/components/select'
import type { Provider, EmailStyleOverride, FrameworkOverride, PsychologyStrategy, ComplianceCheck } from '@/lib/types'
import {
  useEnrichProviderEmail,
  useGenerateOutreachEmail,
  useOutreachRun,
  useCheckCompliance,
  useUpdateProviderStatus,
  useStreamOutreachEmail,
} from '@/hooks/useProviders'

const EMAIL_STYLE_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'Cold', value: 'cold' },
  { label: 'Consultative', value: 'consultative' },
]

const FRAMEWORK_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'TIA', value: 'TIA' },
  { label: 'OPPA', value: 'OPPA' },
  { label: 'QVA', value: 'QVA' },
]

const PSYCHOLOGY_OPTIONS: SelectProps.Option[] = [
  { label: 'Automatic', value: 'automatic' },
  { label: 'Loss Aversion', value: 'loss_aversion' },
  { label: 'Expertise', value: 'expertise' },
  { label: 'Value First', value: 'value_first' },
  { label: 'Partnership', value: 'partnership' },
]

interface OutreachCardProps {
  clientId: string
  provider: Provider
}

type BadgeColor = 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral'

function computeFitScore(p: Provider): { total: number; label: string; color: BadgeColor; talking_points: string[] } {
  let score = 0
  const talking_points: string[] = []
  if (p.prescribing_volume_annual_usd !== null) {
    if (p.prescribing_volume_annual_usd > 100_000) { score += 4; talking_points.push(`$${(p.prescribing_volume_annual_usd/1000).toFixed(0)}k annual Medicare volume`) }
    else if (p.prescribing_volume_annual_usd > 50_000) { score += 2; talking_points.push(`$${(p.prescribing_volume_annual_usd/1000).toFixed(0)}k annual Medicare volume`) }
  }
  if (p.engagement_signals.length > 0) { score += 2; talking_points.push(`${p.engagement_signals.length} prior engagement signal(s)`) }
  if (p.cms_rank_by_volume !== null && p.cms_rank_by_volume <= 100) { score += 1; talking_points.push(`Ranked #${p.cms_rank_by_volume} by Medicare volume`) }
  const total = Math.min(score, 10)
  return { total, label: total >= 7 ? 'Strong Match' : total >= 4 ? 'Good Fit' : 'Low Priority', color: total >= 7 ? 'green' : total >= 4 ? 'blue' : 'grey', talking_points }
}

export default function OutreachCard({ clientId, provider }: OutreachCardProps) {
  const { enrichEmail } = useEnrichProviderEmail()
  const { generateEmail } = useGenerateOutreachEmail()
  const { updateStatus } = useUpdateProviderStatus()
  const { stream } = useStreamOutreachEmail()
  const [email, setEmail] = useState(provider.email)
  const [runId, setRunId] = useState<string | null>(null)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [flash, setFlash] = useState<Array<{ type: 'error' | 'success'; content: string; dismissible?: boolean; onDismiss?: () => void }>>([])
  const [emailStyleOverride, setEmailStyleOverride] = useState<EmailStyleOverride>('automatic')
  const [frameworkOverride, setFrameworkOverride] = useState<FrameworkOverride>('automatic')
  const [psychologyStrategy, setPsychologyStrategy] = useState<PsychologyStrategy>('automatic')
  const [settingsExpanded, setSettingsExpanded] = useState(false)
  const { run } = useOutreachRun(clientId, runId)
  const { checkCompliance, result: compliance } = useCheckCompliance()
  const enrichedRef = useRef(false)

  // Call enrichment once when component mounts if email is missing
  // This is an imperative one-shot trigger, not a data fetch effect
  if (!enrichedRef.current && !provider.email) {
    enrichedRef.current = true
    enrichEmail(clientId, provider._id).then((result) => {
      if (result.data?.enrichProviderEmail.email) {
        setEmail(result.data.enrichProviderEmail.email)
      }
    }).catch(() => {})
  }

  // Allowed: reacting to Apollo poll result (equivalent to subscription side-effect)
  useEffect(() => {
    if (run && run.status === 'complete' && run.email?.subject && run.email?.body) {
      setSubject(run.email.subject)
      setBody(run.email.body)
      if (subject && body) {
        checkCompliance(clientId, provider._id, run.email.subject, run.email.body)
      }
    }
  }, [run, checkCompliance, clientId, provider._id, subject, body])

  // Allowed: reacting to Apollo poll result (equivalent to subscription side-effect)
  useEffect(() => {
    if (!run) return
    if (run.status === 'error') {
      setFlash([{
        type: 'error',
        content: 'Email generation failed. Please try again.',
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
      setRunId(null)
    }
  }, [run?.status])

  const fit = computeFitScore(provider)

  function mapBadgeColor(color: string): 'blue' | 'green' | 'grey' | 'red' | 'severity-critical' | 'severity-high' | 'severity-medium' | 'severity-low' | 'severity-neutral' | undefined {
    switch (color) {
      case 'emerald':
        return 'green'
      case 'amber':
        return 'blue'
      case 'zinc':
        return 'grey'
      default:
        return undefined
    }
  }

  const handleGenerateEmail = async () => {
    setSettingsExpanded(false)
    setFlash([])
    const result = await generateEmail(clientId, provider._id).catch((err: unknown) => {
      setFlash([{
        type: 'error',
        content: err instanceof Error ? err.message : 'Failed to start generation.',
        dismissible: true,
        onDismiss: () => setFlash([]),
      }])
      return null
    })
    if (!result) return
    const id = result.data?.generateOutreachEmail?.runId
    if (id) {
      setRunId(id)
      void stream(clientId, id, provider._id, {
        emailStyleOverride,
        frameworkOverride,
        psychologyStrategy,
      })
    }
  }

  const handleMarkAsSent = async () => {
    await updateStatus(clientId, provider._id, 'contacted', {
      date: new Date().toISOString(),
      signal_type: 'email_sent',
      detail: `Email sent to ${email}`,
    })
    setSuccessMessage('Email marked as sent successfully')
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const isHighSeverity = compliance?.overall_severity === 'HIGH'
  const canSend = !isHighSeverity && subject.trim() && body.trim()

  return (
    <SpaceBetween size="m">
      <ColumnLayout columns={2}>
        <Box>
          <Box variant="small" color="text-label">NPI</Box>
          <Box>{provider.npi}</Box>
        </Box>
        <Box>
          <Box variant="small" color="text-label">Email</Box>
          <Box>{email || '—'}</Box>
        </Box>
        <Box>
          <Box variant="small" color="text-label">CMS Volume</Box>
          <Box>{provider.prescribing_volume_annual_usd ? `$${(provider.prescribing_volume_annual_usd/1000).toFixed(0)}k` : '—'}</Box>
        </Box>
        <Box>
          <Box variant="small" color="text-label">Fit Score</Box>
          <Badge color={mapBadgeColor(fit.color)}>{fit.label}</Badge>
        </Box>
      </ColumnLayout>

      <ExpandableSection headerText="Why they fit" defaultExpanded={false}>
        <SpaceBetween size="xs">
          {fit.talking_points.map(point => (
            <Box key={point} variant="p">• {point}</Box>
          ))}
        </SpaceBetween>
      </ExpandableSection>

      {!subject && !body && !runId && (
        <ExpandableSection
          headerText="Generation Settings"
          expanded={settingsExpanded}
          onChange={({ detail }) => setSettingsExpanded(detail.expanded)}
        >
          <SpaceBetween size="m">
            <ColumnLayout columns={2}>
              <FormField label="Email Style">
                <Select
                  options={EMAIL_STYLE_OPTIONS}
                  selectedOption={EMAIL_STYLE_OPTIONS.find((o) => o.value === emailStyleOverride) ?? null}
                  onChange={({ detail }) => setEmailStyleOverride((detail.selectedOption.value ?? 'automatic') as EmailStyleOverride)}
                />
              </FormField>
              <FormField label="Framework">
                <Select
                  options={FRAMEWORK_OPTIONS}
                  selectedOption={FRAMEWORK_OPTIONS.find((o) => o.value === frameworkOverride) ?? null}
                  onChange={({ detail }) => setFrameworkOverride((detail.selectedOption.value ?? 'automatic') as FrameworkOverride)}
                />
              </FormField>
            </ColumnLayout>
            <FormField label="Psychology Strategy">
              <Select
                options={PSYCHOLOGY_OPTIONS}
                selectedOption={PSYCHOLOGY_OPTIONS.find((o) => o.value === psychologyStrategy) ?? null}
                onChange={({ detail }) => setPsychologyStrategy((detail.selectedOption.value ?? 'automatic') as PsychologyStrategy)}
              />
            </FormField>
          </SpaceBetween>
        </ExpandableSection>
      )}

      <Box>
        <Button onClick={handleGenerateEmail} variant="primary" disabled={!!runId}>
          Generate Email
        </Button>
      </Box>

      {subject && body && (
        <SpaceBetween size="m">
          {/* Read-only summary of settings used to generate this email */}
          <ColumnLayout columns={3} borders="horizontal">
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Email Style</Box>
              <Box>{EMAIL_STYLE_OPTIONS.find(o => o.value === emailStyleOverride)?.label ?? emailStyleOverride}</Box>
            </SpaceBetween>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Framework</Box>
              <Box>{FRAMEWORK_OPTIONS.find(o => o.value === frameworkOverride)?.label ?? frameworkOverride}</Box>
            </SpaceBetween>
            <SpaceBetween size="xs">
              <Box variant="awsui-key-label">Psychology Strategy</Box>
              <Box>{PSYCHOLOGY_OPTIONS.find(o => o.value === psychologyStrategy)?.label ?? psychologyStrategy}</Box>
            </SpaceBetween>
          </ColumnLayout>
          <FormField label="Subject">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.detail.value)}
            />
          </FormField>
          <FormField label="Body">
            <Textarea
              value={body}
              onChange={(e) => setBody(e.detail.value)}
              rows={8}
            />
          </FormField>

          {compliance && (
            <ExpandableSection
              headerText={`Compliance Check (${compliance.overall_severity})`}
              defaultExpanded={true}
            >
              <SpaceBetween size="m">
                <StatusIndicator type={
                  compliance.overall_severity === 'HIGH' ? 'error' :
                  compliance.overall_severity === 'MEDIUM' ? 'warning' :
                  'success'
                }>
                  {compliance.overall_severity}
                </StatusIndicator>
                <SpaceBetween size="xs">
                  {compliance.checks.map((check: ComplianceCheck) => (
                    <Box key={check.label} variant="p">• {check.label}: {check.detail}</Box>
                  ))}
                </SpaceBetween>
                <Button onClick={() => checkCompliance(clientId, provider._id, subject, body)} variant="inline-link">Re-check Compliance</Button>
              </SpaceBetween>
            </ExpandableSection>
          )}

          <Button
            onClick={handleMarkAsSent}
            variant="primary"
            disabled={!canSend}
          >
            Mark as Sent
          </Button>
        </SpaceBetween>
      )}

      {successMessage && (
        <Flashbar items={[{ type: 'success', content: successMessage }]} />
      )}

      {flash.length > 0 && <Flashbar items={flash} />}
    </SpaceBetween>
  )
}
